import 'hammerjs';
import {
  APP_INITIALIZER,
  ApplicationRef,
  CompilerFactory,
  ComponentFactoryResolver,
  Injector,
  NgModule,
  Type
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ResourceModule } from '@ngx-resource/handler-ngx-http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { RecaptchaModule } from 'ng-recaptcha';

//components
import { PageOfferViewComponent } from './page-views/page-offer-view.component';
import { PageCustomerViewComponent } from './page-views/page-customer-view.component';
import { PageDeliveryViewComponent } from './page-views/page-delivery-view.component';
import { PageSummaryViewComponent } from './page-views/page-summary-view.component';
import { PageErrorViewComponent } from './page-views/page-error-view.component';
import { InfoBannerComponent } from './utils/info-banner/info-banner.component';
import { LegalComponent } from './utils/legal/legal.component';
import { InfoCcdsBannerComponent } from './utils/info-banner/info-ccds-banner.component';
import { HeadlineComponent } from './headline/headline.component';
import { NewOfferHeaderComponent } from './customer-offer/new-offer/new-offer-header.component';
import { BankSectionRedirectComponent } from './customer-offer/order-summary/bank-section-redirect.component';
import { UserDataFormComponent } from './customer-form/user-data/user-data-form.component';
import { UserDataDetailsComponent } from './customer-form/user-data/user-data-details.component';
import { OrderDetailsSlotAddonComponent } from './customer-offer/order-details/order-details-slot-addon.component';
import { OrderDetailsDataSlotComponent } from './customer-offer/order-details/order-details-data-slot.component';
import { OrderDetailsAccessoryComponent } from './customer-offer/order-details/order-details-accessory.component';
import { DeliveryModeComponent } from './customer-form/delivery-and-payment/delivery-mode/delivery-mode.component';
import { PaymentModeComponent } from './customer-form/delivery-and-payment/payment-mode/payment-mode.component';
import { UserAuthenticationComponent } from './customer-form/user-authentication-mode/user-authentication.component';
import { AgreementsComponent } from './customer-form/agreements/agreements.component';
import { SummaryPricesComponent } from './customer-offer/summary/summary-prices.component';
import OrderDetailsSlotComponent from './customer-offer/order-details/order-details-slot.component';
import SlotMessageComponent from './utils/slot-message/slot-message.component';
import RecaptchaComponent from './customer-form/recaptcha/recaptcha.component';
import { DocumentsComponent } from './customer-form/documents/documents.component';
import { ContractDisclaimersComponent } from './customer-form/contract-disclaimers/contract-disclaimers.component';
import B2bB2cFormSwitcherComponent from './customer-form/user-data/b2b-b2c-form-switcher.component';
import { Play24CheckoutComponent } from './customer-form/play24-checkout/play24-checkout.component';
import { BohoSideOrderDetailsComponent } from './customer-offer/side-order-details/boho-side-order-details.component';
import { BohoSideOrderDetailsSlotComponent } from './customer-offer/side-order-details/boho-side-order-details-slot.component';
import { BohoSideOrderDetailsAccessoryComponent } from './customer-offer/side-order-details/boho-side-order-details-accessory.component';
import { SideOrderDetailsComponent } from './customer-offer/side-order-details/side-order-details.component';
import { SideOrderDetailsSlotComponent } from './customer-offer/side-order-details/side-order-details-slot.component';
import { SideOrderDetailsSlotAddonComponent } from './customer-offer/side-order-details/side-order-details-slot-addon.component';
import { SideOrderDetailsSlotGadgetComponent } from './customer-offer/side-order-details/side-order-details-slot-gadget.component';
import { SideOrderDetailsAdditionalDataComponent } from './customer-offer/side-order-details/side-order-details-additional-data.component';
import { SideOrderDetailsAccessoryComponent } from './customer-offer/side-order-details/side-order-details-accessory.component';
import { OrderSummaryComponent } from './customer-offer/order-summary/order-summary.component';
import { OrderSummaryReceiverComponent } from './customer-offer/order-summary/order-summary-receiver.component';
import { OrderSummaryWhatHappenNowComponent } from './customer-offer/order-summary/order-summary-what-happen-now.component';
import { OrderSummaryQuestionnaireComponent } from './customer-offer/order-summary/order-summary-questionnaire.component';
import { OrderDetailsSlotGadgetComponent } from './customer-offer/order-details/order-details-slot-gadget.component';
import { CommonModalComponent } from './utils/modals/common-modal.component';
import { WcagComponent } from './customer-actions/wcag.component';
import { UserDataInstallationAddressComponent } from './customer-form/user-data/user-data-installation-address.component';
import { NewOfferSectionComponent } from './customer-offer/new-offer/new-offer-section.component';
import PriceGroupComponent from './prices/price-group.component';
import NewOfferSlotComponent from './customer-offer/new-offer/new-offer-slot.component';
import SlotProcessInputComponent from './customer-offer/new-offer/slot-process-input.component';
import SlotTvInputComponent from './customer-offer/new-offer/slot-tv-input.component';
import SlotBannerComponent from './customer-offer/new-offer/slot-banner.component';
import { NewOfferSlotAddonComponent } from './customer-offer/new-offer/new-offer-slot-addon.component';
import NewOfferSlotAccessoryItemComponent from './customer-offer/new-offer/new-offer-slot-accessory-item.component';
import NewOfferSlotAccessoriesComponent from './customer-offer/new-offer/new-offer-slot-accessories.component';
import { NewOfferTvSectionComponent } from './customer-offer/new-offer/new-offer-tv-section.component';
import { NewOfferDataSectionComponent } from './customer-offer/new-offer/new-offer-data-section.component';
import { NewOfferDataSlotComponent } from './customer-offer/new-offer/new-offer-data-slot.component';
import { NewOfferSlotAddAddonsComponent } from './customer-offer/new-offer/new-offer-slot-add-addons.component';
import { NewOfferSlotGadgetComponent } from './customer-offer/new-offer/new-offer-slot-gadget.component';
import { NewOfferSlotAddOptionalMemberComponent } from './customer-offer/new-offer/new-offer-slot-add-optional-member.component';
import { NewOfferDetailsBusinessWidgetComponent } from './customer-offer/new-offer/new-offer-details-business-widget.component';
import { NewOfferDetailsWidgetComponent } from './customer-offer/new-offer/new-offer-details-widget.component';
import { NewOfferDetailsBusinessSummaryComponent } from './customer-offer/new-offer/new-offer-details-business-summary.component';
import { NewOfferSlotDiscountInfoComponent } from './customer-offer/new-offer/new-offer-slot-discount-info.component';
import { SideOrderDiscountInfoComponent } from './customer-offer/side-order-details/side-order-discount-info.component';
import { HeaderComponent } from './header/header.component';
import { SlotContractLengthComponent } from './customer-offer/new-offer/slot-contract-length.component';
import { FormFieldComponent } from './customer-form/form-helpers/form-field/form-field.component';
import { CalendarComponent } from './customer-form/form-helpers/calendar/calendar.component';

//pipes
import { MonthForm } from './utils/pipes/monthForm';
import { RechargingDeclination } from './utils/pipes/rechargingDeclination';
import MsisdnFormatClear from './utils/pipes/msisdnFormatClear';
import { NumberDeclensionFormatPipe } from './utils/pipes/number-declension-format.pipe';
import { MaxOptionalMembersPipe, OptionalMembersPipe } from './utils/pipes/optional-member.pipe';
import { SimCartFormatPipe } from './utils/pipes/simCartFormat.pipe';
import { DevicesAndAccessoriesPipe } from './utils/pipes/devices-and-accessories.pipe';
import DateFormatPipe from './utils/pipes/date-format.pipe';
//services
import { NotificationProviderService } from './external-interfaces/notification-provider.service';
import { FormStateChangeService } from './customer-form/form-state/form-state-change.service';
import { DeliveryFormStateService } from './customer-form/form-state/delivery-form-state.service';
import { setAppInjector } from './utils/services/set-app-injector';
import { DetailsModalService } from './services/details-modal.service';
import SlotService from './cart-process/slot.service';
import { SummaryPricesTextsService } from './customer-offer/summary/summary-prices-texts.service';
import { SlotsValidationService } from './order-fulfillment/slots-validation.service';
import { PagesValidationService } from './order-fulfillment/pages-validation.service';
import { ExternalInterfacesService } from './external-interfaces/external-interfaces.service';
import { LoadersService } from './utils/loaders/loaders.service';
import { ModalLoaderService } from './utils/loaders/modal-loader.service';
import { MapAddressService } from './customer-form/delivery-and-payment/delivery-mode/pos/map-address.service';
import { DeliveryModalService } from './customer-form/delivery-and-payment/delivery-mode/pos/delivery-modal.service';
import { DeliveryPickupService } from './customer-form/delivery-and-payment/delivery-pickup.service';
import { UiHelperService } from './utils/services/ui-helper.service';
import { AgreementsService } from './customer-form/agreements/agreements.service';
import { CommonInfoService } from './cart-process/common-info.service';
import ManageTvService from './customer-offer/manage-tv/manage-tv.service';
import { MsisdnHelperService } from './utils/services/msisdn-helper.service';
import RecaptchaService from './customer-form/recaptcha/recaptcha.service';
import StockValidationService from './cart-process/stock-validation.service';
import { FormFieldsMasks } from './customer-form/field-mask/form-fields-masks';
import B2bB2cFormSwitcherService from './customer-form/user-data/b2b-b2c-form-switcher.service';
import MessagesActionsService from './utils/services/messages-actions.service';
import CustomerDetailsService from './cart-process/customer-details.service';
import SlotStateService from './cart-process/slot-state.service';
import { LeadsSupportService } from './customer-offer/auth/leads-support.service';
import OtpAuthService from './customer-offer/auth/otp-auth.service';
import SsoAuthService from './customer-offer/auth/sso-auth.service';
import MnpAuthService from './customer-offer/auth/mnp-auth.service';
import OtherProcessAuthService from './customer-offer/auth/other-process-auth.service';
import QueueService from './external-interfaces/queue.service';
import EndpointService from './external-interfaces/endpoint.service';
import FormFieldsSchemaService from './customer-form/form-helpers/form-fields-schema.service';
import { OptionalMemberService } from './cart-process/optional-member.service';
import { CommonGadgetsService } from './customer-offer/order-gadgets/common-gadgets.service';
import { DataLayerService } from './customer-actions/data-layer.service';
import { CartClearConfirmationService } from './services/cart-clear-confirmation.service';
import { SelectedAgreementsService } from './customer-form/agreements/selected-agreements.service';
import { SubmitOrderService } from './order-fulfillment/submit-order.service';
import { UserDataFormService } from './customer-form/user-data/user-data-form.service';
import { CommonAddonsService } from './customer-offer/order-addons/common-addons.service';
import { SlotAddonsService } from './customer-offer/order-addons/slot-addons.service';
import { PrimeAddonsService } from './customer-offer/order-addons/prime-addons.service';
import { CommonModalService } from './utils/modals/common-modal.service';
import { CommonModalDataService } from './utils/modals/common-modal-data.service';
import DrawingNewNumberService from './customer-offer/draw-number/drawing-new-number.service';
import { WcagService } from './customer-actions/wcag.service';
import { CheckoutService } from './order-fulfillment/checkout.service';
import { YourOfferService } from './cart-process/your-offer.service';
import { SummaryPricesService } from './customer-offer/summary/summary-prices.service';
import { LogoutModalService } from './services/logoutModalService';
import { PosPrecartPickupService } from './customer-form/delivery-and-payment/delivery-mode/pos/pos-precart-pickup.service';
import P4FacadeService from './utils/services/p4-facade.service';
import ValidationRulesService from './utils/services/validation-rules.service';
import { FormStepsService } from './customer-form/form-steps/form-steps.service';
import { ConfigService } from './cart-process/config.service';
import AccessoriesService from './cart-process/accessories.service';
import { ProvinceSelectModalService } from './services/province-select-modal.service';

//directives
import { ClickTrigger } from './utils/directives/click-trigger.directive';
import { SharedModule } from './shared.module';
import { PageScrollerDirective } from './utils/directives/page-scroller.directive';
import { DynamicAttributesDirective } from './customer-form/form-helpers/form-dynamic-attributes.directive';

declare const window: any;

export function createCompiler(compilerFactory: CompilerFactory) {
  return compilerFactory.createCompiler();
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, window.themeResourcePath + '/lang/' + 'PLAY_', '.json');
}

export function getConfig(configService: ConfigService) {
  return (): Promise<any> => {
    return configService.init();
  };
}

const moduleConfig = {
  entryComponents: [
    PageOfferViewComponent,
    PageCustomerViewComponent,
    PageDeliveryViewComponent,
    PageSummaryViewComponent,
    Play24CheckoutComponent,
    PageErrorViewComponent,
  ]
};

@NgModule({
  declarations: [
    PageOfferViewComponent,
    PageCustomerViewComponent,
    PageDeliveryViewComponent,
    PageSummaryViewComponent,
    PageErrorViewComponent,
    NewOfferSlotDiscountInfoComponent,
    NewOfferDetailsBusinessSummaryComponent,
    NewOfferDetailsBusinessWidgetComponent,
    NewOfferDetailsWidgetComponent,
    NewOfferSlotAddOptionalMemberComponent,
    NewOfferSlotGadgetComponent,
    NewOfferSlotAddAddonsComponent,
    NewOfferTvSectionComponent,
    NewOfferDataSectionComponent,
    NewOfferDataSlotComponent,
    NewOfferSlotAccessoriesComponent,
    NewOfferSlotAccessoryItemComponent,
    InfoBannerComponent,
    InfoCcdsBannerComponent,
    HeadlineComponent,
    NewOfferHeaderComponent,
    NewOfferSectionComponent,
    NewOfferSlotComponent,
    SlotContractLengthComponent,
    SlotProcessInputComponent,
    SlotTvInputComponent,
    SlotBannerComponent,
    NewOfferSlotAddonComponent,
    OrderDetailsSlotAddonComponent,
    OrderDetailsSlotGadgetComponent,
    BankSectionRedirectComponent,
    UserDataFormComponent,
    UserDataDetailsComponent,
    OrderDetailsDataSlotComponent,
    OrderSummaryComponent,
    OrderSummaryReceiverComponent,
    OrderSummaryWhatHappenNowComponent,
    OrderSummaryQuestionnaireComponent,
    DeliveryModeComponent,
    PaymentModeComponent,
    UserAuthenticationComponent,
    OrderDetailsAccessoryComponent,
    BohoSideOrderDetailsComponent,
    BohoSideOrderDetailsSlotComponent,
    BohoSideOrderDetailsAccessoryComponent,
    SideOrderDiscountInfoComponent,
    SideOrderDetailsComponent,
    SideOrderDetailsSlotComponent,
    SideOrderDetailsSlotAddonComponent,
    SideOrderDetailsSlotGadgetComponent,
    SideOrderDetailsAdditionalDataComponent,
    SideOrderDetailsAccessoryComponent,
    PriceGroupComponent,
    AgreementsComponent,
    SummaryPricesComponent,
    OrderDetailsSlotComponent,
    SlotMessageComponent,
    RecaptchaComponent,
    DocumentsComponent,
    ContractDisclaimersComponent,
    B2bB2cFormSwitcherComponent,
    FormFieldComponent,
    Play24CheckoutComponent,
    ClickTrigger,
    CommonModalComponent,
    PageScrollerDirective,
    DynamicAttributesDirective,
    WcagComponent,
    UserDataInstallationAddressComponent,
    HeaderComponent,
    LegalComponent,
    CalendarComponent,

    MonthForm,
    RechargingDeclination,
    MsisdnFormatClear,
    OptionalMembersPipe,
    MaxOptionalMembersPipe,
    NumberDeclensionFormatPipe,
    SimCartFormatPipe,
    DevicesAndAccessoriesPipe,
    DateFormatPipe,
  ],
  imports: [
    SharedModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    HttpClientModule,
    RecaptchaModule,
    ResourceModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: 'pl',
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    // ServiceWorkerModule.register('/_ui/desktop/theme-blue/multi/public/app/ngsw-worker.js', { enabled: environment.production }),
  ],
  providers: [
    LogoutModalService,
    DetailsModalService,
    DeliveryFormStateService,
    FormStateChangeService,
    SlotService,
    AccessoriesService,
    StockValidationService,
    SummaryPricesTextsService,
    SlotsValidationService,
    PagesValidationService,
    ExternalInterfacesService,
    NotificationProviderService,
    DeliveryPickupService,
    LoadersService,
    ModalLoaderService,
    MapAddressService,
    DeliveryModalService,
    UiHelperService,
    ManageTvService,
    AgreementsService,
    CommonInfoService,
    MsisdnHelperService,
    FormFieldsMasks,
    RecaptchaService,
    B2bB2cFormSwitcherService,
    SlotStateService,
    MessagesActionsService,
    CustomerDetailsService,
    LeadsSupportService,
    OtpAuthService,
    SsoAuthService,
    MnpAuthService,
    OtherProcessAuthService,
    QueueService,
    FormFieldsSchemaService,
    OptionalMemberService,
    CommonGadgetsService,
    DataLayerService,
    CartClearConfirmationService,
    UserDataFormService,
    SelectedAgreementsService,
    SubmitOrderService,
    CommonAddonsService,
    SlotAddonsService,
    PrimeAddonsService,
    CommonModalService,
    CommonModalDataService,
    DrawingNewNumberService,
    WcagService,
    CheckoutService,
    YourOfferService,
    SummaryPricesService,
    PosPrecartPickupService,
    P4FacadeService,
    FormStepsService,
    ProvinceSelectModalService,
    ValidationRulesService,
    {
      provide: 'Endpoint', useFactory: () => {
        return (endpointName: string, params: object) => {
          return new EndpointService(endpointName, params);
        };
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: getConfig,
      deps: [
        ConfigService
      ],
      multi: true
    }
  ],
})
export class AppModule {
  constructor(private injector: Injector, private resolver: ComponentFactoryResolver) {
    setAppInjector(injector);
  }

  ngDoBootstrap(appRef: ApplicationRef) {
    moduleConfig.entryComponents.forEach((componentDef: Type<{}>) => {
      const factory = this.resolver.resolveComponentFactory(componentDef);
      if (document.querySelector(factory.selector)) {
        appRef.bootstrap(factory);
      }
    });
  }
}


